import {Injectable} from '@angular/core';
import {AppUserClaimsService} from './app-user-claims.service';
import {AdminService} from './admin.service';
import {DirectorService} from './director.service';
import {OrdersService} from './orders.service';

/*
The Support Portal pages are for internal Mitel employees that perform support for our customers and partners.
They are given a special token that is restricted to only allow access to data that has been approved by
our legal/security teams. 
Do not make changes to this file unless the requirement has been thoroughly reviewed for the Support Portal and
the policy restricting the tokens has been udpdated as required.
*/
@Injectable({
	providedIn: 'root'
})
export class SupportPortalService {
    /*
    Support users will need to have the tag 'supportRoles' with the role name e.g licenseSupport
    in order to see the option enabled in the role dropdown.
    To properly use the role, the user will need the corresponding policy.
    In the future, some policies may allow for multiple roles.
    */
    private supportRoles = [
        {name: 'licenseSupport', disabled: true},
        {name: 'productSupport', disabled: true}, 
        {name: 'designSupport', disabled: true}
    ];

    /*
    Will store the Support Role the user last chose.
    This will save them from having to manually selecting it
    everytime they return to the search page
    */
    private currentSupportRole = null;

    /*
    When the user first logs in, save their partnerId in order to prevent
    them from supporting their own partner/account
    */
    private usersSupportPartnerId = null;
    
    /*
    Some support teams are global - they need access to all clouds.
    Clouds are enabled based on a user's supportRegions tags.
    There may be users that only have access to some clouds but not all.
    The supportRegions tags don't control whether the user can actually log in to a different cloud.
    The supportRegions tags are used simply to facilitate cloud switching by redirecting to the auth portal
    with the accountID of interest.
    The idRegion value matches the director response when searching identities by org_id
    */
    private supportClouds = [
        {name: 'us-east-1', disabled: true, idRegion: 'us'}, 
        {name: 'eu-central-1', disabled: true, idRegion: 'eu'}, 
        {name: 'ap-southeast-2', disabled: true, idRegion: 'ap'}
    ];

    /*
    Depending on the support role, the user can search by different keys.
    */
    allowedSearchKeys = {
        licenseSupport: [
            {name: 'sapId', disabled: false},
            {name: 'organizationId', disabled: false},
            {name: 'subscriptionNumber', disabled: false},
            {name: 'partnerId', disabled: false},
            {name: 'customerAccountNumber', disabled: false}
        ],
        productSupport: [
            {name: 'partnerId', disabled: false},
            {name: 'customerAccountNumber', disabled: false},
            {name: 'userId', disabled: false},
            {name: 'userEmail', disabled: false, additional: 'accountId'}
        ]
    };

    /*
    Depending on the support role, there may be support options to perform on a partner, 
    support options to perform on a customer, and support options to perform on a user. 
    Each support option will unlock some routes in accounts app.
    Never should an enabled support option have an empty route array.
    For a support option, the first route it has listed will be used as the initial route
    to navigate to.
    In the app-routing module, route guards are often used for more than one route. 
    We don't necessarily want to unlock all the routes associated with that route guard.
    We only want to unlock the routes we need for the support option chosen.
    Don't add more routes unless thorough analysis has been performed, and the policy for Support Portal users 
    has been updated to load the page correctly in Read-Only mode.
    */
    private supportOptions = {
        licenseSupport: {
            partner: [
                {name: 'orderSummary', disabled: false, routes: ['/orders']}
            ],
            customer: [
                {name: 'licenseSummary', disabled: false, disableWhenPrime: true, routes: ['/accounts/:id/subscriptions']}
            ]
        },
        productSupport: {
            partner: [],
            customer: [
                //{name: 'supportLogs', disabled: true, disableWhenPrime: false, routes: ['/accounts/:id/supportlogs']},
                {name: 'integrationsList', disabled: false, disableWhenPrime: true, 
                    routes: ['/accounts/:id/integrations']} //, '/accounts/:id/gateway']} not allowing access into GWP yet
            ],
            user: [
                {name: 'userDetails', disabled: false, routes: [
                    '/accounts/:id/users/:userid', 
                    '/accounts/:id/users/:userid/product-mitelOne'
                ]}
            ]
        }
    };

    private defaultCloud = undefined;
    private unlockedRoutes = [];

    constructor(
		private claimsService: AppUserClaimsService,
        private adminService: AdminService,
        private directorService: DirectorService,
        private ordersService: OrdersService
	) {}

    getCurrentSupportRole(){
        return this.currentSupportRole;
    }
    setCurrentSupportRole(role){
        this.currentSupportRole = role;
    }

    getUsersSupportPartnerId(){
        return this.usersSupportPartnerId;
    }
    setUsersSupportPartnerId(id: string){
        this.usersSupportPartnerId = id;
    }

    getUnlockedRoutes(){
        return this.unlockedRoutes;
    }
    resetUnlockedRoutes(){
        this.unlockedRoutes = [];
    }

    /*
    Find the selected support option and get the corresponding routes.
    Substitute in the account id where it's required.
    */
    setUnlockedRoutes(role: string, type: string, name: string, data = {}){
        this.resetUnlockedRoutes();
        let option = null;
        if(this.supportOptions[role] && this.supportOptions[role][type]){
            option = this.supportOptions[role][type].find(obj => {
                return obj.name === name
            });
            if(option){
                option.routes.forEach(route => {
                    for (const [key, value] of Object.entries(data)) {
                        console.log(`${key}: ${value}`);
                        if(route.includes(key)){
                            route = route.replace(key, value);
                        }
                    }
                    console.log("route with ids: ", route);
                    this.unlockedRoutes.push(route)
                });
            }
        }
        console.log("option: ", option);
        console.log("unlockedroutes: ", this.unlockedRoutes);
    }

    /*
    The inital route will always be the first route listed in the unlocked routes.
    This method returns the first unlocked route in the proper format for the menuservice
    E.g. '/accounts/:id/subscriptions' --> ['accounts', :id, 'subscriptions']
    */
    getInitialRoute(){
        let initialRoute = [];
        if (this.unlockedRoutes.length>0){
            let pieces = this.unlockedRoutes[0].split("/");
            if(pieces.length>1)
            {
                pieces.shift(); //since the routes start with a slash, index 0 will always be blank - remove it.
                initialRoute.push(...pieces);
            }
        }
        console.log("Initial route: ", initialRoute);
        return initialRoute;
    }

    getSupportRoles(){
        return this.supportRoles;
    }

    //sets disabled to true for all roles
    resetSupportRoles(){
        this.supportRoles.forEach(role =>{
            role.disabled = true;
        });
    }

    /*
    Will set supportRoles based on user tags
    */
    setSupportRoles(tags: any){
        this.resetSupportRoles();
        if(tags && tags['supportRoles']){
            this.supportRoles.forEach(role =>{
                if(tags['supportRoles'].includes(role.name)){
                    role.disabled = false;
                }
            });  
        }
    }

    getDefaultCloud(){
        return this.defaultCloud;
    }
    
    setDefaultCloud(cloud: any){
        this.defaultCloud = cloud;
    }

    getSupportClouds(){
        return this.supportClouds;
    }

    //sets disabled to true for all clouds
    resetSupportClouds(){
        this.supportClouds.forEach(cloud =>{
            cloud.disabled = true;
        });
    }

    /*
    Only enable a cloud option if the user has the corresponding supportRegions tag.
    If we enable the cloud option that matches the cloud in the user's claims - set it as the default
    cloud option (it's the cloud they are currently in).
    If the user does not have the tag for the cloud in which they are logged in, a mistake has been made creating the user.
    I may consider logging out the user in this case - TBD.
    But for now, as soon as they pick a cloud in the dropdown list, they will get logged out anyways.
    */
    setSupportClouds(tags: any){
        let claimsCloud = this.claimsService.getClaimsCloud();
        this.defaultCloud = undefined;
        this.resetSupportClouds();
        if(tags && tags.supportRegions && Array.isArray(tags.supportRegions) && tags.supportRegions.length > 0){
            this.supportClouds.forEach(cloud => {
                if(tags.supportRegions.includes(cloud.name)){
                    cloud.disabled = false;
                    if(claimsCloud && claimsCloud === cloud.name){
                        this.setDefaultCloud(cloud);
                    }
                }
            });
        }
        console.log("setSupportClouds: ", this.supportClouds);
    }

    setupSupportPortalPermissions(user: any){
        let tags = undefined;       
        if(user && user.tags){
           tags = user.tags;
        }
        this.setSupportRoles(tags);
        this.setSupportClouds(tags);
    }

    getAllowedSearchKeys(supportRole: string){
        return this.allowedSearchKeys[supportRole]? this.allowedSearchKeys[supportRole] : [];
    }

    getSupportOptions(role: string, type: string){
        return this.supportOptions[role] && this.supportOptions[role][type]? this.supportOptions[role][type] : [];
    }

    findPartner(searchValue: string): Promise<any>{
        let partnerData = {
            partner: null,
            active: null,
            error: null
        };
        return this.adminService.getPartner(searchValue).then( partner => {
            console.log("Found partner: ", partner);
            partnerData.partner = partner;
            let str = partner.active.toString().toUpperCase();
            partnerData["active"] = str;
            return partnerData;
        })
        .catch(err => {
            console.log("Failed to get partner:", searchValue,  err);
            if(err.statusCode && err.statusCode === 404){
                partnerData.error = {message: 'not_found'};
            } else{
                partnerData.error = {message: 'generic'}; 
            }
          return partnerData;
        });
        
    }

    findAccount(accountId: string, type: string): Promise<any>{
        let accountData = {
            account: null, //leave account unaltered since it will be passed to companySwitcherService
            address: null,
            active: null,
            orgId: null,
            error: null,
            contacts: null // are now a separate data item
        };
        return this.adminService.getAccountWithoutFrills(accountId, 'tags').then( account => {
            accountData.account = account;
            console.log(`Found ${type} account: `, account);
            let str = account.active.toString().toUpperCase();
            accountData["active"] = str;

            accountData["address"] = this.formatAddress(account);
            
            //'any' because organizationId currently not in Account interface from jsdk
            accountData["orgId"] = (account as any).organizationId? (account as any).organizationId : '';
            // support contacts are no longer returned in the accounts
            // object. We must request them separately
            return this.adminService.getAllContactsForAccountId(accountId).then( contacts => {
                accountData["contacts"] = contacts;
                return accountData;
            })
            .catch(err => { // won't allow a contact error to prevent account data from being returned
                console.log("Error requesting support contacts", err);
                return accountData;
            });
        })
        .catch(err => {
            console.log(`Failed to get ${type} account:`, accountId,  err);
            if(err.statusCode && err.statusCode === 404){
                accountData.error = {message: 'not_found'};
            }
            else{
                accountData.error = {message: 'generic'}; 
            }
            
            return accountData;
        });
    }

    formatAddress(account: any): any{    

        let rawAddress = account && account.location && account.location.address? account.location.address : null;
        let address = {
            line1: '', //street
            line2: '', //street2
            line3: '', //city, province/state, country
            line4: ''  //zipcode
        }
        let templine3 = [];

        if(rawAddress){
            address.line1 = rawAddress.street? rawAddress.street : '';
            address.line2 = rawAddress.street2? rawAddress.street2 : '';

            if(rawAddress.city) 
                templine3.push(rawAddress.city);
            if(rawAddress.stateOrProvince) 
                templine3.push(rawAddress.stateOrProvince);
            if(rawAddress.country) 
                templine3.push(rawAddress.country);

            address.line3 = templine3.length > 0? templine3.join(', ') : '';

            address.line4 = rawAddress.zipCode? rawAddress.zipCode : '';
        }
        return address;
    }

    // accounts?$select=accountBasicInfo&$searchKeys=primeAccountId&$search=______
    // accounts?$select=accountBasicInfo&$searchKeys=subscriptionNumber&$search=______&$expand=customerAccountNumber
    // accounts?$select=accountBasicInfo&$searchKeys=sapId&$search=______
    searchForBillingAccounts(searchKey: string, value: string): Promise<any>{
        let selectParam = 'accountBasicInfo';
        let allowedBillingKeys = ['primeAccountId', 'subscriptionNumber', 'sapId'];
        let searchKeyParam = allowedBillingKeys.includes(searchKey)? searchKey :  null;
        let expand = searchKeyParam === 'subscriptionNumber'? 'customerAccountNumber' : null;
        let searchValue = value && value != ''? value : null;
        let billingData = {
            count: null,
            billingAccounts: null,
            assignedCustomerAccountNumber: null,
            error: null
        };

        if(searchKeyParam && searchValue){
            return this.ordersService.getBasicBillingAccountInfo(selectParam, searchKeyParam, searchValue, expand).then( billinAccounts => {
                let formattedData = this.formatBillingAccounts(billinAccounts);
                billingData.count = formattedData.count;
                billingData.billingAccounts = formattedData.accounts;
                billingData.assignedCustomerAccountNumber = formattedData["assignedCustomerAccountNumber"];
        
                return billingData;
            }).catch(err => {
                console.log("Failed to get billing accounts:", searchValue,  err);
                if(err.statusCode && err.statusCode === 404){
                    billingData.error = {message: 'not_found'};
                } else{
                    billingData.error = {message: 'generic'};
                }
                return billingData;
            });
        }
        else {
            billingData.error = {message: 'invalid_search'};
            return Promise.resolve(billingData);
        }
    }

    formatBillingAccounts(rawData){
        let billingData = {
            count: 0,
            assignedCustomerAccountNumber: null,
            accounts: [],
        };
        if (rawData && rawData.count && rawData.count > 0){
            billingData.count = rawData.count;
            rawData._embedded.items.forEach(rawAccount => {
                let account = {
                    billingId: rawAccount.basicInfo && rawAccount.basicInfo.id ? rawAccount.basicInfo.id : null,
                    billingNumber: rawAccount.basicInfo && rawAccount.basicInfo.accountNumber ? rawAccount.basicInfo.accountNumber : null,
                    primeAccountId: rawAccount.basicInfo && rawAccount.basicInfo.accountId ? rawAccount.basicInfo.accountId : null,
                    status: rawAccount.basicInfo && rawAccount.basicInfo.status ? rawAccount.basicInfo.status : null,
                    currency: rawAccount.basicInfo && rawAccount.basicInfo.currency ? rawAccount.basicInfo.currency : null,
                    sapId: rawAccount.basicInfo && rawAccount.basicInfo.sapId ? rawAccount.basicInfo.sapId : null,
                };
                billingData.accounts.push(account);          
            });
            billingData.assignedCustomerAccountNumber = this.getAssignedCustomerAccountNumber(rawData._embedded.items[0]);
        }
        return billingData;
    }

    getAssignedCustomerAccountNumber(data){
        let accountNumber = null;
        if(data && data._embedded && data._embedded.subscriptions &&
            data._embedded.subscriptions._embedded && 
            data._embedded.subscriptions._embedded.items &&
            data._embedded.subscriptions._embedded.items.length > 0){
                let subscription = data._embedded.subscriptions._embedded.items[0];
                if(subscription.customerCloudlinkAccountNumber && subscription.customerCloudlinkAccountNumber != ''){
                    accountNumber = subscription.customerCloudlinkAccountNumber;
                }
        }
        return accountNumber;
    }

    getIdentityByOrgId(orgId: string){
        let identityData = {
            count: null,
            identities: null,
            error: null
        }
        return this.directorService.getIdentities(orgId, 'ORG_ID').then(data => {
            if(data && data.count && data.count === 1){ //maybe and also that there is a uuid
                identityData.count = data.count;
                identityData.identities = data.identities;
                let identityCloud = data.identities[0].idRegion? data.identities[0].idRegion : null; //e.g us

                if(identityCloud){
                    //see if the account is in the current cloud or if the support user needs to switch clouds
                    console.log('default cloud:', this.defaultCloud);
                    if(this.defaultCloud && this.defaultCloud['idRegion'] === identityCloud){
                        console.log('Identity found in current cloud');
                    }
                    else{
                        //support person will need to switch clouds to identity cloud
                        let destinationCloud = this.supportClouds.find(cloud =>{
                            return cloud.idRegion === identityCloud;
                        });
                        if (destinationCloud){
                            identityData.error = {
                                message: 'switch_clouds',
                                details: 'cloudRegions.'+ destinationCloud.name // translation key for cloud name
                            };
                        }
                        else {
                            identityData.error = {
                                message: 'cloud_unknown'
                            };
                        }
                    }

                }
                else{
                    identityData.error = {
                        message: 'cloud_unknown'
                    };
                }
            }
            else if(data && data.count && data.count > 1){
                identityData.count = data.count;
                identityData.error = {
                    message: 'multiple_results'
                };
            }
            else{
                identityData.count = 0;
                identityData.error = {
                    message: 'not_found'
                };
            }
            return identityData;
        })
        .catch(err => {
            identityData.error = {
                message: 'generic'
            };
            return identityData;
        });
    }

    /*
    Can't use ADMIN GET /user + search params because don't want to allow this route - too much data
    ADMIN GET /users/{userId} looks for exact match in email, userId or mobile number.
    */
    findUserByUserId(searchValue: string): Promise<any>{
        let userData = {
            users: [],
            count: null,
            error: null
        };

        if(searchValue && searchValue != ''){
            return this.adminService.getUserWithoutFrills(searchValue).then(rawData =>{
                console.log("Search by userId result:", rawData);
                if(rawData){
                    userData.count = 1;
                    let user = this.getFormattedUser(rawData);
                    userData.users.push(user);
                }
                return userData;
            }).catch(err =>{
                console.log("Search by userId ERROR:", err);
                if(err.statusCode && err.statusCode === 409){
                    userData.error = {message: 'multiple_results'};
                    /*
                    - multiple users found
                    - could be if user did not enter a unique id (perhaps email, mobile, etc)
                    - director doesn't allow identity search by unique id
                    - since we don't know what type searchValue was given, could do an identity 
                    search without a type - but really the user is not using the search as intended.
                    - could be an enhancement for later
                    */
                }
                else if (err.statusCode && err.statusCode === 404){
                    userData.error = {message: 'not_found'};
                    /*
                    - the user could exist in a different cloud
                    - if director allowed for indentity search by unique id could tell user
                    which cloud to switch to
                    - could be an enhancement for later
                    */
                }
                else{
                    userData.error = {message: 'generic'};
                }
                return userData;
            });
        }
        else{
            console.log("Invalid user search");
            userData.error = {message:'invalid_search'};
            return Promise.resolve(userData);
        }
    }

    /*
    Search via ADMIN GET /accounts/{accountId}/users/{userId}
    {accountId} can be id or number
    For {userId}, ADMIN looks for exact match in email, userId or mobile number
    If multiples are found, use Director (since can't use GET /users?$Search)
    */
    findUserInAccountByEmail(email: string, accountId: string): Promise<any>{
        let userData = {
            users: [],
            count: null,
            error: null
        };

        if(email && email != '' && accountId && accountId != ''){
            return this.adminService.getUserForAccountNoFrills(accountId, email).then(rawData =>{
                console.log("Search by email + accountId result:", rawData);
                if(rawData){
                    userData.count = 1;
                    let user = this.getFormattedUser(rawData);
                    userData.users.push(user);
                }
                return userData;
            }).catch(err =>{
                console.log("Search by email + accountId ERROR:", err);
                if(err.statusCode && err.statusCode === 409){
                    /* 
                    - multiple users found
                    - could use director to find their uuids
                    - but will need to filter director results for just the users in this account
                    - will need to get the accountId not the accountNumber for idOwner comparison
                    - user may have supplied accountNumber
                    */
                   
                    //the value in accountId could be an accountNumber, need to get actual accountId
                    return this.adminService.getAccountWithoutFrills(accountId).then( async account => {
                        userData = await this.findUserIdentitiesByEmail(email, account.accountId)
                        return userData;

                    }).catch(err => {
                        //really should not fail - otherwise the getUserForAccountNoFrills would have failed
                        console.log("Getting Account Number failed: ", err);
                        userData.error = {message: 'generic'};
                        return userData;
                    });
                }
                else if (err.statusCode && err.statusCode === 404){
                    if(err.body && err.body.name && err.body.name === 'AccountNotFound'){
                        /* 
                        - account does not exist in current cloud
                        - director ACCOUNT search works for account_number and not account_id
                        - if director search worked for both, could find which cloud to help with cloud switching
                        - potential enhancement
                        */
                        userData.error = {message: 'account_not_found'}
                    }
                    else{
                        userData.error = {message: 'user_not_found_in_account'}
                    }
                }
                else{
                    userData.error = {message: 'generic'};
                }
                return userData;
            });
        }
        else{
            console.log("Invalid user search");
            userData.error = {message:'invalid_search'};
            return Promise.resolve(userData);
        }
    }
    
    /*
    Use Director to get the identites with the given email
    Set type to EMAIL so it definitely searches that field - will be an exact match
    Will need to process identity data to get the full user profiles for those of interest
    In some cases, will send along an accountId to filter out some identity results 
    */
    findUserIdentitiesByEmail(email: string, accountId?: string): Promise<any>{
        let userData = {
            users: [],
            count: null,
            error: null,
            usersInEachCloud: null
        };

        if(email && email != ''){
            return this.directorService.getIdentities(email, 'EMAIL').then(rawData =>{
                console.log("Search identities by email result:", rawData);
                if (rawData && rawData.count && rawData.count >= 1) {
                     return this.processUserIdentityData(rawData, accountId).then(userData => {
                         return userData;
                     });
                }
                else{
                    userData.error = {message: 'not_found'};
                    return userData;
                }
            }).catch(err =>{
                console.log("Search identities by email ERROR:", err);
                userData.error = {message: 'generic'};
                return userData;
            });
        }
        else{
            console.log("Invalid user search");
            userData.error = {message:'invalid_search'};
            return Promise.resolve(userData);
        }
    }

    /*
    Process identity results from Director search.
    Will tally up relevant results that appear in all clouds
    Will only get profiles of users who belong to current cloud (token restriction)
    */
    async processUserIdentityData(rawData: any, accountId?: string): Promise<any>{
        /*
        count -> number of full profiles returned from Admin micro for those in current cloud and in account (if provided)
        users -> array of full profiles returned from Admin micro for those in current cloud and in account (if provided)
        usersInEachCloud -> total counts found by director (for info purposes) of users with that email and in account (if provided)
        */
        let userData = {
            users: [],
            count: 0,
            error: null,
            usersInEachCloud: {
                us: 0,
                eu: 0,
                ap: 0
            }
        };
        let rawUser = null;
        let user = null;
        let keepUserCloudCount = false;

        if(rawData && rawData.count && rawData.count > 0 && rawData.identities){
            for (const identity of rawData.identities) {
                if(identity.uuid && identity.uuid != '' && 
                (!accountId || (accountId && accountId === identity.idOwner))){
                    switch (identity.idRegion) {
                        case 'us':
                            ++userData.usersInEachCloud.us;
                            break;
                        case 'eu':
                            ++userData.usersInEachCloud.eu;
                            break;
                        case 'ap':
                            ++userData.usersInEachCloud.ap;
                            break;
                    }
                    /*
                    Only get the full user profile via ADMIN micro for the users in the current cloud.
                    The token is restrcited to current cloud for ADMIN requests.
                    */
                    if(this.defaultCloud && this.defaultCloud['idRegion'] === identity.idRegion){
                        rawUser = null;
                        user = null;
                        try{
                            rawUser = await this.adminService.getUserWithoutFrills(identity.uuid);
                            if(rawUser){
                                user = this.getFormattedUser(rawUser);
                                userData.users.push(user);
                                ++userData.count;
                            }
                        }
                        catch(err){
                            console.log('problem finding user: ', identity.uuid);
                        }
                    }
                }
            }

            /*
            If no users were found in clouds other than the current cloud, clear usersInEachCloud.
            There's no point in reporting it.
            Otherwise set an error to inform that relevant users exist in other clouds
            */
            console.log("Cloud count: ", userData.usersInEachCloud);
            
            for (let [cloud, count] of Object.entries(userData.usersInEachCloud)) {
                if(this.defaultCloud && this.defaultCloud['idRegion'] != cloud && count >0){
                    keepUserCloudCount = true;
                    break;
                }
            }
        }

        if(!keepUserCloudCount){
            userData.usersInEachCloud = null;
        }
        else{
            userData.error = {
                message: "users_in_other_clouds", 
                details: "us: " + userData.usersInEachCloud.us + 
                ", eu: " + userData.usersInEachCloud.eu +
                ", ap: " + userData.usersInEachCloud.ap
            }
        }
        //console.log("processed userData: ", userData);
        return Promise.resolve(userData);
    }

    /*
    At this point just want to captialize the 'active' property.
    May want to do more in the future
    */
    getFormattedUser(rawUser:any){
        let user = JSON.parse(JSON.stringify(rawUser));
        user.active = user.active.toString().toUpperCase();
        return user;
    }
}